<script setup lang="ts">
import { SancareButton } from '@sancare/typescript-commons'
import { ButtonColorVariant, ButtonVariant } from '@sancare/typescript-commons/src/components/SancareButton.vue'
import { onMounted, ref, useId, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import { EvaluationFeedbackItem, EvaluationFeedbackVerdict } from '@/misc/types'

const { t } = useI18n()

const props = withDefaults(defineProps<{
  // The connected user's feedback being changed, null when giving a first one
  feedback?: EvaluationFeedbackItem | null
  // Who the evaluation comes from ("l'agent" or a user's name), asked about in the form's question
  evaluationAuthor: string
  isSaving?: boolean
}>(), {
  feedback: null,
  isSaving: false,
})

const emit = defineEmits<{
  save: [payload: { verdict: EvaluationFeedbackVerdict, comment: string | null }]
  remove: []
  cancel: []
}>()

// Same colors as the verdicts of the evaluations themselves
const VERDICT_COLORS: Record<EvaluationFeedbackVerdict, ButtonColorVariant> = {
  agree: ButtonColorVariant.PURPLE,
  disagree: ButtonColorVariant.RED,
  debatable: ButtonColorVariant.ORANGE,
}

const VERDICTS: EvaluationFeedbackVerdict[] = ['agree', 'disagree', 'debatable']

const verdict = ref<EvaluationFeedbackVerdict | null>(props.feedback?.verdict ?? null)
const comment = ref(props.feedback?.comment ?? '')
const verdictGroup = useTemplateRef<HTMLDivElement>('verdictGroup')
const commentInput = useTemplateRef<HTMLTextAreaElement>('commentInput')
const questionId = useId()

// The whole form can be filled with the keyboard once opened: the verdicts are walked through with
// the arrows (as a radio group), then picked with space or enter, which moves on to the comment.

function focusVerdict(index: number) {
  const buttons = Array.from(verdictGroup.value?.querySelectorAll<HTMLElement>('.evaluation-feedback-verdict') ?? [])
  buttons[(index + buttons.length) % buttons.length]?.focus()
}

function moveVerdictFocus(step: number) {
  const focused = verdictGroup.value?.querySelector('.evaluation-feedback-verdict:focus')
  focusVerdict(VERDICTS.findIndex((choice) => focused?.getAttribute('data-verdict') === choice) + step)
}

onMounted(() => focusVerdict(verdict.value ? VERDICTS.indexOf(verdict.value) : 0))

// The comment comes right after the verdict
function chooseVerdict(choice: EvaluationFeedbackVerdict) {
  verdict.value = choice
  commentInput.value?.focus()
}

function save() {
  if (verdict.value !== null) {
    emit('save', { verdict: verdict.value, comment: comment.value.trim() || null })
  }
}
</script>

<template>
  <form
    class="evaluation-feedback-form mb-3"
    @submit.prevent="save"
  >
    <p
      :id="questionId"
      class="mb-2 evaluation-feedback-question"
    >
      {{ t('stayDisplayer.justificationSummary.evaluation.feedbackQuestion', { author: evaluationAuthor }) }}
    </p>
    <div
      ref="verdictGroup"
      class="d-flex gap-2 mb-2"
      role="radiogroup"
      :aria-labelledby="questionId"
      @keydown.left.prevent="moveVerdictFocus(-1)"
      @keydown.right.prevent="moveVerdictFocus(1)"
    >
      <sancare-button
        v-for="(color, choice) in VERDICT_COLORS"
        :key="choice"
        :text="t(`stayDisplayer.justificationSummary.evaluation.feedbackVerdict.${choice}`)"
        :color="color"
        :variant="verdict === choice ? ButtonVariant.FILLED : ButtonVariant.OUTLINED"
        :is-disabled="isSaving"
        class="focus-ring evaluation-feedback-verdict"
        role="radio"
        tabindex="0"
        :aria-checked="verdict === choice"
        :data-verdict="choice"
        @click="chooseVerdict(choice)"
        @keydown.enter.space.prevent="chooseVerdict(choice)"
      />
    </div>
    <!-- Enter goes to the line, ctrl (cmd on Mac) + Enter saves -->
    <textarea
      ref="commentInput"
      v-model="comment"
      class="form-control"
      rows="2"
      :disabled="isSaving"
      :placeholder="t('stayDisplayer.justificationSummary.evaluation.commentPlaceholder')"
      @keydown.ctrl.enter.prevent="save"
      @keydown.meta.enter.prevent="save"
    />
    <div class="d-flex gap-2 mt-2">
      <sancare-button
        v-if="feedback"
        :text="t('stayDisplayer.justificationSummary.evaluation.removeFeedback')"
        :variant="ButtonVariant.OUTLINED"
        :is-disabled="isSaving"
        class="evaluation-feedback-remove"
        @click="emit('remove')"
      />
      <sancare-button
        :text="t('stayDisplayer.justificationSummary.evaluation.cancel')"
        :variant="ButtonVariant.OUTLINED"
        :is-disabled="isSaving"
        class="ms-auto evaluation-feedback-cancel"
        @click="emit('cancel')"
      />
      <sancare-button
        :text="t('stayDisplayer.justificationSummary.evaluation.save')"
        :color="ButtonColorVariant.PURPLE"
        :is-disabled="verdict === null"
        :is-loading="isSaving"
        class="evaluation-feedback-save"
        @click="save"
      />
    </div>
  </form>
</template>

